/**
 * VersionTag — small, low-contrast version display.
 *
 * Apr 30 2026 — extracted from DesktopLayout's inline impl so the
 * mobile, tablet, and Capacitor layouts can show the version too.
 * Per user request: "I want to see what version I'm currently
 * working on on the front end, and confirm backend matches".
 *
 * Renders compact in-place: "v2.1.0" by default. Tappable for a
 * detail tooltip showing:
 *   • Frontend semver (compiled-in via Vite at build time)
 *   • Frontend build SHA (from dist/version.json fetched at runtime)
 *   • Backend version (from /api/version, fetched once on mount)
 *   • Drift warning if frontend and backend disagree
 *
 * The detail view is intentionally minimal — clicking the tag opens
 * a small popover that closes when you click outside. We NEVER
 * surface secrets here; just the two version strings + build SHAs.
 *
 * Performance: the API fetch is fire-and-forget on mount with a 3s
 * timeout. If it fails (rare — it's an unauthenticated endpoint),
 * the popover just shows "backend: ?" and the tag still renders.
 * No retries, no caching beyond the component's lifetime.
 */
import { useState, useEffect, useRef } from 'react';
import api from '../../services/api.js';

const FRONTEND_VERSION = import.meta.env.VITE_APP_VERSION || '0.0.0';

export default function VersionTag({ inline = false, style = {} }) {
  const [open, setOpen] = useState(false);
  const [backend, setBackend] = useState(null); // { version, build, startedAt }
  const [frontendBuild, setFrontendBuild] = useState(null); // from /version.json
  const [error, setError] = useState(null);
  // Apr 30 2026 — wrapperRef covers BOTH the trigger button AND the
  // popover so the outside-click handler doesn't fire when the user
  // taps the button again to close. Using popover-only ref made
  // tapping the button race with the toggle (mousedown closed it,
  // then click reopened). Wrapping fixes the race.
  const wrapperRef = useRef(null);

  // Fetch backend version once on mount. We don't poll — that's the
  // job of useAppVersionPoll which already exists for the
  // "update available" banner. This is just a snapshot for display.
  useEffect(() => {
    let cancelled = false;
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 3000);

    api.get('/version', { signal: ctrl.signal })
      .then(({ data }) => {
        // Apr 30 2026 — sanity check the response shape before
        // committing. If we got HTML (e.g. a 502 from nginx falling
        // back to index.html via try_files) or some other unexpected
        // payload, treat as "unreachable" rather than rendering
        // garbage like "vundefined".
        if (!cancelled) {
          if (data && typeof data === 'object' && typeof data.version === 'string') {
            setBackend(data);
          } else {
            setError('malformed response');
          }
        }
      })
      .catch(err => {
        if (!cancelled && err.name !== 'CanceledError' && err.name !== 'AbortError') {
          setError(err.message || 'fetch failed');
        }
      })
      .finally(() => clearTimeout(t));

    // Frontend build SHA lives in /version.json (emitted at build
    // time by the vite plugin). Fetch with cache: 'no-store' so we
    // see the actual deployed version even if the SW has stale
    // cached HTML/JS.
    fetch('/version.json', { cache: 'no-store' })
      .then(r => r.ok ? r.json() : null)
      .then(j => { if (!cancelled && j) setFrontendBuild(j); })
      .catch(() => { /* non-fatal — we'll just show semver only */ });

    return () => { cancelled = true; ctrl.abort(); clearTimeout(t); };
  }, []);

  // Close popover on outside click. The wrapperRef covers both the
  // trigger button and the popover, so tapping the button to toggle
  // closed doesn't get pre-empted by this handler.
  // Apr 30 2026 — using pointerdown (not mousedown) to cover both
  // mouse and touch devices uniformly. mousedown is synthesized on
  // touch but with timing quirks on some browsers; pointerdown is
  // the modern unified event.
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    // Defer one tick so the click that opened the popover doesn't
    // immediately close it (the opening pointer event bubbles up to
    // document AFTER React's synthetic onClick runs).
    const t = setTimeout(() => document.addEventListener('pointerdown', handler), 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener('pointerdown', handler);
    };
  }, [open]);

  // Drift detection — frontend and backend semver should match. If
  // they don't, the deploy was partial or the user has stale HTML.
  // Triggers a yellow ! icon so the user/coach notices something off.
  const drift = backend && backend.version && backend.version !== FRONTEND_VERSION;

  const tagBase = {
    textAlign: 'center',
    fontSize: 9,
    fontFamily: 'var(--font-display)',
    letterSpacing: '0.08em',
    color: drift ? '#ffa726' : 'var(--text-3)',
    opacity: 0.55,
    userSelect: 'none',
    cursor: 'pointer',
    padding: inline ? '2px 6px' : '6px 8px 2px',
    background: 'transparent',
    border: 'none',
    transition: 'opacity 0.15s',
    ...style,
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative', display: inline ? 'inline-block' : 'block' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        onMouseEnter={(e) => e.currentTarget.style.opacity = '0.85'}
        onMouseLeave={(e) => e.currentTarget.style.opacity = '0.55'}
        title={`Game Day v${FRONTEND_VERSION}${drift ? ' — backend mismatch!' : ''}`}
        style={tagBase}>
        {drift ? '⚠ ' : ''}v{FRONTEND_VERSION}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Version details"
          style={{
            position: 'absolute',
            bottom: 'calc(100% + 6px)',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 600,
            background: 'var(--surface, #0d1520)',
            border: '1px solid var(--border, rgba(255,255,255,0.1))',
            borderRadius: 10,
            padding: '10px 12px',
            // Apr 30 2026 — minWidth tuned to fit the narrowest
            // sidebar (TabletLayout = 200px in portrait). maxWidth
            // caps growth so backend names like "v2.10.123 · abc1234"
            // don't blow out the popover. The container's parent
            // gives natural overflow protection on phones.
            minWidth: 180,
            maxWidth: 240,
            fontSize: 11,
            fontFamily: 'var(--font-body, sans-serif)',
            color: 'var(--text, #e6e6e6)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
            lineHeight: 1.55,
            textAlign: 'left',
            // Defensive: if labels somehow overflow, break them
            // gracefully rather than truncating mid-word.
            wordBreak: 'break-word',
          }}>
          <div style={{
            fontFamily: 'var(--font-display)',
            fontSize: 10, fontWeight: 700, letterSpacing: '0.1em',
            textTransform: 'uppercase', color: 'var(--text-2)',
            marginBottom: 6,
          }}>
            Build Info
          </div>

          {/* Frontend row */}
          <div style={{ marginBottom: 4 }}>
            <span style={{ color: 'var(--text-2)' }}>Frontend:</span>{' '}
            <strong>v{FRONTEND_VERSION}</strong>
            {frontendBuild?.build && (
              <span style={{ color: 'var(--text-3)', fontSize: 10 }}>
                {' '}· {frontendBuild.build}
              </span>
            )}
          </div>

          {/* Backend row */}
          <div style={{ marginBottom: 4 }}>
            <span style={{ color: 'var(--text-2)' }}>Backend:</span>{' '}
            {backend ? (
              <>
                <strong>v{backend.version}</strong>
                {backend.build && backend.build !== 'unknown' && (
                  <span style={{ color: 'var(--text-3)', fontSize: 10 }}>
                    {' '}· {backend.build}
                  </span>
                )}
              </>
            ) : (
              <span style={{ color: 'var(--text-3)' }}>
                {error ? '(unreachable)' : 'loading…'}
              </span>
            )}
          </div>

          {/* Built-at row, frontend only — backend reports startedAt
              instead since it doesn't know its own image's build time. */}
          {frontendBuild?.builtAt && (
            <div style={{ marginBottom: 4, color: 'var(--text-3)', fontSize: 10 }}>
              Built {new Date(frontendBuild.builtAt).toLocaleString()}
            </div>
          )}
          {backend?.startedAt && (
            <div style={{ color: 'var(--text-3)', fontSize: 10 }}>
              API up since {new Date(backend.startedAt).toLocaleString()}
            </div>
          )}

          {drift && (
            <div style={{
              marginTop: 8, padding: '6px 8px', borderRadius: 6,
              background: 'rgba(255,167,38,0.12)',
              border: '1px solid rgba(255,167,38,0.3)',
              color: '#ffa726', fontSize: 10, lineHeight: 1.4,
            }}>
              ⚠ Frontend and backend versions don't match.
              The deploy may be partial, or you may need to refresh
              to pick up the latest UI.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
